import React, { useMemo } from 'react';

import { Box, Paper, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';

import TabPanelShell from '~/components/Offers/Detail/OfferBuilder/shared/TabPanelShell';

import {
  useOtaSummaryContext,
  usePackageOptionsContext,
  usePropertySetupContext,
} from '../../PricingToolSessionContext';
import { PRICING_OPTION_LABELS, audMoney } from '../../constants';

import { NO_PRV_NOTE, type OptionPricingSummary, computeOptionPricingSummary } from './pricingSummaryModel';
import { computePrvThresholdColor } from './prvThresholdColor';

function fmtMoney(value: number | null): string {
  return value != null ? audMoney(value) : '';
}

function fmtPct(value: number | null): string {
  return value != null ? `${value.toFixed(1)}%` : '';
}

function stayMismatchText({ workbookNights, leadNights }: NonNullable<OptionPricingSummary['stayMismatch']>): string {
  return workbookNights === null
    ? `The workbook's Nights in Scrape isn't set, so its PRV can't be checked against the ${leadNights}-night lead and isn't shown.`
    : `PRV from the workbook is for a ${workbookNights}-night stay, but the lead is ${leadNights} nights, so it isn't shown.`;
}

export default function PricingSummaryTab() {
  const { optionStates } = usePackageOptionsContext();
  const { options: workbookSummaries } = useOtaSummaryContext();
  const {
    fx: { currency: netCurrency, audToSourceCurrencyRate },
  } = usePropertySetupContext();

  const summaries = useMemo(
    () =>
      PRICING_OPTION_LABELS.map((_, optionIndex) =>
        computeOptionPricingSummary({
          optionIndex,
          option: optionStates[optionIndex],
          workbookSummary: workbookSummaries[optionIndex],
          netCurrency,
          audToSourceCurrencyRate,
        }),
      ),
    [optionStates, workbookSummaries, netCurrency, audToSourceCurrencyRate],
  );

  return (
    <TabPanelShell
      subtitle="Compare the three options side by side — sell price, margin, PRV and the month-by-month chart."
      title="Pricing Summary"
    >
      <Typography color="text.secondary" sx={{ mb: 2 }} variant="caption">
        PRV and the months come from the OTA Rates sheet of the uploaded workbook, worked out at the sell it had then.
        Upload the workbook again after changing a lead sell.
      </Typography>
      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' } }}>
        {summaries.map((summary) => (
          <Paper key={summary.optionIndex} sx={{ p: 2 }} variant="outlined">
            <Typography fontWeight={600} sx={{ mb: 1 }} variant="subtitle1">
              {PRICING_OPTION_LABELS[summary.optionIndex]}
            </Typography>
            <Stack spacing={0.5} sx={{ mb: 2 }}>
              <Typography variant="body2">Sell: {fmtMoney(summary.sell)}</Typography>
              <Typography variant="body2">Margin: {fmtPct(summary.marginPct)}</Typography>
              <Typography
                sx={summary.prvPct != null ? computePrvThresholdColor(summary.prvPct) : undefined}
                variant="body2"
              >
                PRV: {fmtPct(summary.prvPct)}
              </Typography>
              {summary.stayMismatch && (
                <Typography color="warning.main" variant="caption">
                  {stayMismatchText(summary.stayMismatch)}
                </Typography>
              )}
              {!workbookSummaries[summary.optionIndex] && (
                <Typography color="text.secondary" variant="caption">
                  {NO_PRV_NOTE}
                </Typography>
              )}
            </Stack>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Month</TableCell>
                  <TableCell align="right">PRV %</TableCell>
                  <TableCell align="right">Avg OTA</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {summary.monthRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} />
                  </TableRow>
                ) : (
                  summary.monthRows.map((row) => (
                    <TableRow key={row.label}>
                      <TableCell>{row.label}</TableCell>
                      <TableCell
                        align="right"
                        sx={row.medianPrvPct != null ? computePrvThresholdColor(row.medianPrvPct) : undefined}
                      >
                        {fmtPct(row.medianPrvPct)}
                      </TableCell>
                      <TableCell align="right">{fmtMoney(row.avgOta)}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Paper>
        ))}
      </Box>
    </TabPanelShell>
  );
}
